<template>
  <div class="admin-reviews-container">
    <div v-if="!hasSupportAccess" class="access-denied-card">
      <div class="access-denied-icon"><i class="fa-solid fa-lock"></i></div>
      <h3>Access restricted</h3>
      <p>Only super admin or support can moderate seller reviews.</p>
      <p class="access-denied-hint">Sign in with an admin account that has access to support tools.</p>
    </div>

    <div v-else class="admin-content">
      <!-- HEADER -->
      <div class="admin-header">
        <h1><i class="fa-solid fa-star-half-alt"></i> Seller Reviews Management</h1>
        <p class="subtitle">Moderate and manage all seller reviews</p>
      </div>

      <!-- STATS CARDS -->
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-icon total">
            <i class="fa-solid fa-star"></i>
          </div>
          <div class="stat-content">
            <h3>{{ totalReviews }}</h3>
            <p>Total Reviews</p>
          </div>
        </div>
        
        <div class="stat-card">
          <div class="stat-icon pending">
            <i class="fa-solid fa-clock"></i>
          </div>
          <div class="stat-content">
            <h3>{{ pendingReviews }}</h3>
            <p>Pending Review</p>
          </div>
        </div>
        
        <div class="stat-card">
          <div class="stat-icon approved">
            <i class="fa-solid fa-check-circle"></i>
          </div>
          <div class="stat-content">
            <h3>{{ approvedReviews }}</h3>
            <p>Approved Reviews</p>
          </div>
        </div>
        
        <div class="stat-card">
          <div class="stat-icon rejected">
            <i class="fa-solid fa-ban"></i>
          </div>
          <div class="stat-content">
            <h3>{{ rejectedReviews }}</h3>
            <p>Rejected Reviews</p>
          </div>
        </div>
      </div>

      <!-- FILTERS & CONTROLS -->
      <div class="controls-section">
        <div class="filters-row">
          <div class="search-box">
            <i class="fa-solid fa-search"></i>
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="Search by seller name, reviewer, or review content..."
              @input="handleSearch"
            />
          </div>
          
          <div class="filter-group">
            <select v-model="statusFilter" @change="filterReviews" class="filter-select">
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            
            <select v-model="ratingFilter" @change="filterReviews" class="filter-select">
              <option value="all">All Ratings</option>
              <option value="5">5 Stars</option>
              <option value="4">4 Stars</option>
              <option value="3">3 Stars</option>
              <option value="2">2 Stars</option>
              <option value="1">1 Star</option>
            </select>
            
            <button class="btn-refresh" @click="fetchAllReviews">
              <i class="fa-solid fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
              Refresh
            </button>
          </div>
        </div>
        
        <div class="date-filter-row">
          <div class="date-input-group">
            <label><i class="fa-solid fa-calendar"></i> From:</label>
            <input type="date" v-model="dateFrom" @change="filterReviews" />
          </div>
          
          <div class="date-input-group">
            <label><i class="fa-solid fa-calendar"></i> To:</label>
            <input type="date" v-model="dateTo" @change="filterReviews" />
          </div>
          
          <button class="btn-clear-filters" @click="clearFilters">
            <i class="fa-solid fa-times"></i>
            Clear Filters
          </button>
        </div>
      </div>

      <!-- REVIEWS TABLE -->
      <div class="reviews-table-container">
        <div v-if="loading" class="loading-table">
          <i class="fa-solid fa-spinner fa-spin"></i>
          <p>Loading reviews...</p>
        </div>
        
        <div v-else-if="filteredReviews.length === 0" class="no-reviews">
          <i class="fa-solid fa-inbox"></i>
          <h3>No reviews found</h3>
          <p>Try adjusting your filters or search terms</p>
        </div>
        
        <div v-else class="table-responsive">
          <table class="reviews-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Seller</th>
                <th>Reviewer</th>
                <th>Rating</th>
                <th>Review</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="review in paginatedReviews" :key="review.review_id" :class="getRowClass(review)">
                <td class="review-id">#{{ review.review_id }}</td>
                <td class="seller-info">
                  <div class="seller-avatar">
                    {{ getInitials(review.seller_name || 'Seller') }}
                  </div>
                  <div class="seller-details">
                    <strong>{{ review.seller_name || 'Unknown Seller' }}</strong>
                    <small>ID: {{ review.seller_id }}</small>
                  </div>
                </td>
                <td class="reviewer-info">
                  <div class="reviewer-avatar">
                    {{ getInitials(review.reviewer_name) }}
                  </div>
                  <div class="reviewer-details">
                    <strong>{{ review.reviewer_name }}</strong>
                    <small>ID: {{ review.reviewer_id }}</small>
                  </div>
                </td>
                <td class="rating-cell">
                  <div class="star-display">
                    <i 
                      v-for="n in 5" 
                      :key="n" 
                      :class="n <= review.rating ? 'fa-solid fa-star' : 'fa-regular fa-star'"
                      :style="{ color: n <= review.rating ? getRatingColor(review.rating) : '#ddd' }"
                    ></i>
                  </div>
                  <span class="rating-text">{{ review.rating }}/5</span>
                </td>
                <td class="review-content-cell">
                  <div class="review-content">
                    <strong v-if="review.title">{{ review.title }}</strong>
                    <p class="review-comment">{{ truncateText(review.comment, 100) }}</p>
                    <div v-if="review.is_verified_purchase" class="verified-badge">
                      <i class="fa-solid fa-circle-check"></i> Verified Purchase
                    </div>
                  </div>
                </td>
                <td class="status-cell">
                  <span :class="`status-badge status-${review.status}`">
                    <i :class="getStatusIcon(review.status)"></i>
                    {{ formatStatus(review.status) }}
                  </span>
                </td>
                <td class="date-cell">
                  {{ formatDate(review.created_at) }}
                  <br>
                  <small>{{ formatTime(review.created_at) }}</small>
                </td>
                <td class="actions-cell">
                  <div class="action-buttons">
                    <!-- VIEW DETAILS BUTTON -->
                    <button 
                      class="btn-action btn-view" 
                      @click="viewReviewDetails(review)"
                      title="View Details"
                    >
                      <i class="fa-solid fa-eye"></i>
                    </button>
                    
                    <!-- REJECT BUTTON (only for pending reviews) -->
                    <button 
                      v-if="review.status === 'pending'"
                      class="btn-action btn-reject" 
                      @click="rejectReview(review)"
                      title="Reject Review"
                      :disabled="actionLoading"
                    >
                      <i class="fa-solid fa-ban"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- PAGINATION -->
        <div v-if="filteredReviews.length > 0" class="pagination-controls">
          <div class="pagination-info">
            Showing {{ startItem }}-{{ endItem }} of {{ filteredReviews.length }} reviews
          </div>
          <div class="pagination-buttons">
            <button 
              @click="prevPage" 
              :disabled="currentPage === 1"
              class="pagination-btn"
            >
              <i class="fa-solid fa-chevron-left"></i> Previous
            </button>
            
            <div class="page-numbers">
              <button 
                v-for="page in totalPages" 
                :key="page"
                @click="goToPage(page)"
                :class="{ active: page === currentPage }"
                class="page-btn"
              >
                {{ page }}
              </button>
            </div>
            
            <button 
              @click="nextPage" 
              :disabled="currentPage === totalPages"
              class="pagination-btn"
            >
              Next <i class="fa-solid fa-chevron-right"></i>
            </button>
          </div>
          
          <div class="page-size-selector">
            <label>Show:</label>
            <select v-model="pageSize" @change="resetPagination" class="page-size-select">
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
        </div>
      </div>

      <!-- DETAILS MODAL -->
      <div v-if="showDetailsModal" class="modal-overlay" @click.self="closeDetailsModal">
        <div class="modal-content">
          <div class="modal-header">
            <h3><i class="fa-solid fa-file-alt"></i> Review Details</h3>
            <button class="modal-close" @click="closeDetailsModal">
              <i class="fa-solid fa-times"></i>
            </button>
          </div>
          
          <div class="modal-body">
            <div v-if="selectedReview" class="review-details">
              <!-- REVIEW HEADER -->
              <div class="details-header">
                <div class="review-id-badge">
                  #{{ selectedReview.review_id }}
                </div>
                <div class="review-status" :class="`status-${selectedReview.status}`">
                  {{ formatStatus(selectedReview.status) }}
                </div>
              </div>
              
              <!-- SELLER & REVIEWER INFO -->
              <div class="info-grid">
                <div class="info-card">
                  <h4><i class="fa-solid fa-user-tie"></i> Seller Information</h4>
                  <div class="info-content">
                    <div class="avatar-large">
                      {{ getInitials(selectedReview.seller_name || 'Seller') }}
                    </div>
                    <div>
                      <h5>{{ selectedReview.seller_name || 'Unknown Seller' }}</h5>
                      <p>ID: {{ selectedReview.seller_id }}</p>
                    </div>
                  </div>
                </div>
                
                <div class="info-card">
                  <h4><i class="fa-solid fa-user"></i> Reviewer Information</h4>
                  <div class="info-content">
                    <div class="avatar-large">
                      {{ getInitials(selectedReview.reviewer_name) }}
                    </div>
                    <div>
                      <h5>{{ selectedReview.reviewer_name }}</h5>
                      <p>ID: {{ selectedReview.reviewer_id }}</p>
                      <div v-if="selectedReview.is_verified_purchase" class="verified-tag">
                        <i class="fa-solid fa-circle-check"></i> Verified Purchase
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- RATING -->
              <div class="rating-section">
                <h4><i class="fa-solid fa-star"></i> Rating</h4>
                <div class="rating-display-large">
                  <div class="stars-large">
                    <i 
                      v-for="n in 5" 
                      :key="n" 
                      :class="n <= selectedReview.rating ? 'fa-solid fa-star' : 'fa-regular fa-star'"
                      :style="{ color: n <= selectedReview.rating ? getRatingColor(selectedReview.rating) : '#ddd' }"
                    ></i>
                  </div>
                  <div class="rating-text-large">
                    <span class="rating-number">{{ selectedReview.rating }}</span>
                    <span class="rating-out-of">/5</span>
                    <span class="rating-label">{{ getRatingLabel(selectedReview.rating) }}</span>
                  </div>
                </div>
              </div>
              
              <!-- REVIEW CONTENT -->
              <div class="review-content-section">
                <h4><i class="fa-solid fa-comment"></i> Review Content</h4>
                <div class="review-content-card">
                  <h5 v-if="selectedReview.title">{{ selectedReview.title }}</h5>
                  <p class="full-review">{{ selectedReview.comment }}</p>
                  <div class="review-meta">
                    <span><i class="fa-solid fa-calendar"></i> {{ formatDate(selectedReview.created_at) }}</span>
                    <span><i class="fa-solid fa-clock"></i> {{ formatTime(selectedReview.created_at) }}</span>
                  </div>
                </div>
              </div>
              
              <!-- ADMIN ACTIONS -->
              <div v-if="selectedReview.status === 'pending'" class="admin-actions-section">
                <h4><i class="fa-solid fa-cogs"></i> Admin Actions</h4>
                <div class="action-buttons-row">
                  <button 
                    class="btn-modal btn-modal-reject" 
                    @click="rejectReview(selectedReview)"
                    :disabled="actionLoading"
                  >
                    <i class="fa-solid fa-ban"></i> Reject Review
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- CONFIRMATION MODAL -->
<!-- CONFIRMATION MODAL -->
<div v-if="showConfirmModal" class="modal-overlay" @click.self="closeConfirmModal">
  <div class="confirm-modal">
    <div class="confirm-header">
      <i class="fa-solid fa-ban" style="color: #dc3545;"></i>
      <h3>Reject Review</h3>
    </div>
    
    <div class="confirm-body">
      <p>Are you sure you want to reject this review? This will hide it from public view and notify the reviewer.</p>
      <div v-if="reviewToAction" class="review-preview">
        <p><strong>Reviewer:</strong> {{ reviewToAction.reviewer_name }}</p>
        <p><strong>Seller:</strong> {{ reviewToAction.seller_name || 'Unknown Seller' }}</p>
        <p><strong>Rating:</strong> {{ reviewToAction.rating }}/5</p>
        <p v-if="reviewToAction.title"><strong>Title:</strong> {{ reviewToAction.title }}</p>
      </div>
    </div>
    
    <div class="confirm-footer">
      <button class="btn-cancel" @click="closeConfirmModal" :disabled="actionLoading">
        Cancel
      </button>
      <button 
        class="btn-confirm btn-confirm-reject" 
        @click="executeAction"
        :disabled="actionLoading"
      >
        <template v-if="actionLoading">
          <i class="fa-solid fa-spinner fa-spin"></i>
          Processing...
        </template>
        <template v-else>
          Yes, Reject Review
        </template>
      </button>
    </div>
  </div>
</div>

    </div>
  </div>
</template>

<script>
import { useAdminAuthStore } from "@/authen/adminAuth";
import { buildApiUrl } from "@/config/api";
import globalToastMixin from "@/mixins/globalToastMixin";

export default {
  mixins: [globalToastMixin],
  name: "AdminSellerReviews",

  data() {
    return {
      loading: false,
      actionLoading: false,
      allReviews: [],
      filteredReviews: [],
      searchQuery: "",
      statusFilter: "all",
      ratingFilter: "all",
      dateFrom: "",
      dateTo: "",
      
      // Pagination
      currentPage: 1,
      pageSize: 10,
      
      // Modals
      showDetailsModal: false,
      showConfirmModal: false,
      selectedReview: null,
      reviewToAction: null,
      
      // Statistics (will be fetched from API)
      stats: {
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0
      },
      
    };
  },

  computed: {
    hasSupportAccess() {
      const store = useAdminAuthStore();
      return store.canAccessSupportAdminTools;
    },
    // Statistics from fetched stats
    totalReviews() {
      return this.stats.total;
    },
    
    pendingReviews() {
      return this.stats.pending;
    },
    
    approvedReviews() {
      return this.stats.approved;
    },
    
    rejectedReviews() {
      return this.stats.rejected;
    },
    
    // Pagination calculations (for client-side pagination of filtered data)
    totalPages() {
      return Math.ceil(this.filteredReviews.length / this.pageSize);
    },
    
    startItem() {
      return ((this.currentPage - 1) * this.pageSize) + 1;
    },
    
    endItem() {
      const end = this.currentPage * this.pageSize;
      return end > this.filteredReviews.length ? this.filteredReviews.length : end;
    },
    
    paginatedReviews() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.filteredReviews.slice(start, end);
    }
  },

  async mounted() {
    const store = useAdminAuthStore();
    store.checkToken();
    if (!this.hasSupportAccess) return;
    await this.fetchStatistics();
    await this.fetchAllReviews();
  },

  methods: {
    // API Methods — admin JWT only (super_admin / support); same store as admin panel login
    getAuthHeaders() {
      const store = useAdminAuthStore();
      store.checkToken();
      const token = store.accessToken;
      return {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Request-ID": this.generateUUID(),
      };
    },
    
    generateUUID() {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    },

    async fetchStatistics() {
      if (!this.hasSupportAccess) return;
      try {
        const headers = this.getAuthHeaders();
        const response = await fetch(buildApiUrl("/seller-review/admin/stats"), {
          method: "GET",
          headers,
        });
        
        if (response.ok) {
          const statsData = await response.json();
          this.stats = {
            total: statsData.total_reviews,
            pending: statsData.pending_reviews,
            approved: statsData.approved_reviews,
            rejected: statsData.rejected_reviews
          };
        } else {
          console.error('Failed to fetch statistics');
          // Fallback to calculating from local data
          this.calculateLocalStats();
        }
      } catch (error) {
        console.error('Error fetching statistics:', error);
        this.calculateLocalStats();
      }
    },

    calculateLocalStats() {
      this.stats = {
        total: this.allReviews.length,
        pending: this.allReviews.filter(r => r.status === 'pending').length,
        approved: this.allReviews.filter(r => r.status === 'approved').length,
        rejected: this.allReviews.filter(r => r.status === 'rejected').length
      };
    },

    async fetchAllReviews() {
      if (!this.hasSupportAccess) {
        this.loading = false;
        return;
      }
      this.loading = true;
      try {
        const headers = this.getAuthHeaders();

        const params = new URLSearchParams();

        if (this.statusFilter !== "all") {
          params.append("status_filter", this.statusFilter);
        }

        if (this.ratingFilter !== "all") {
          params.append("rating_filter", String(parseInt(this.ratingFilter, 10)));
        }

        if (this.dateFrom) {
          params.append("date_from", this.dateFrom);
        }

        if (this.dateTo) {
          params.append("date_to", this.dateTo);
        }

        if (this.searchQuery.trim()) {
          params.append("search", this.searchQuery.trim());
        }

        // Load up to 500 per request (backend cap). Table pagination is client-side.
        params.append("page", "1");
        params.append("page_size", "500");

        const url = buildApiUrl(`/seller-review/admin/reviews?${params.toString()}`);

        const response = await fetch(url, {
          method: "GET",
          headers,
        });

        if (response.ok) {
          const reviews = await response.json();
          const list = Array.isArray(reviews) ? reviews : [];
          this.allReviews = list;
          this.filteredReviews = list;
          this.showToast("success", "Success", `Loaded ${list.length} reviews`);
        } else {
          let msg = `Failed to fetch reviews (${response.status})`;
          try {
            const err = await response.json();
            if (err.detail) {
              msg = Array.isArray(err.detail)
                ? err.detail.map((d) => d.msg || d).join(" ")
                : String(err.detail);
            }
          } catch {
            /* ignore */
          }
          throw new Error(msg);
        }
      } catch (error) {
        console.error("Error fetching reviews:", error);
        this.showToast("error", "Error", error.message || "Failed to load reviews");
      } finally {
        this.loading = false;
      }
    },

    async rejectReviewAPI(reviewId) {
      this.actionLoading = true;
      try {
        const headers = this.getAuthHeaders();
        const response = await fetch(
          buildApiUrl(`/seller-review/admin/reviews/${reviewId}/reject`),
          {
            method: "POST",
            headers,
            body: JSON.stringify({
              admin_notes: `Rejected by admin at ${new Date().toISOString()}`,
              reason: "Violation of community guidelines",
            }),
          }
        );
        
        if (response.ok) {
          const updatedReview = await response.json();
          
          // Update local state
          const index = this.allReviews.findIndex(r => r.review_id === reviewId);
          if (index !== -1) {
            this.allReviews[index] = updatedReview;
          }
          
          // Update filtered reviews
          const filteredIndex = this.filteredReviews.findIndex(r => r.review_id === reviewId);
          if (filteredIndex !== -1) {
            this.filteredReviews[filteredIndex] = updatedReview;
          }
          
          // Refresh statistics
          await this.fetchStatistics();
          
          this.showToast('success', 'Success', 'Review rejected successfully');
          this.closeConfirmModal();
          this.closeDetailsModal();
          
          return true;
        } else {
          const error = await response.json();
          throw new Error(error.detail || 'Failed to reject review');
        }
      } catch (error) {
        console.error('Error rejecting review:', error);
        this.showToast('error', 'Error', error.message || 'Failed to reject review');
        return false;
      } finally {
        this.actionLoading = false;
      }
    },

    // Filter Methods (now mostly handled by server, but keep for client-side fallback)
    filterReviews() {
      // Instead of filtering locally, fetch from server with filters
      this.currentPage = 1; // Reset to first page
      this.fetchAllReviews();
    },

    handleSearch() {
      // Debounce search to avoid too many requests
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.filterReviews();
      }, 500);
    },

    clearFilters() {
      this.searchQuery = "";
      this.statusFilter = "all";
      this.ratingFilter = "all";
      this.dateFrom = "";
      this.dateTo = "";
      this.filterReviews();
    },

    // Action Methods
    rejectReview(review) {
      this.reviewToAction = review;
      this.showConfirmModal = true;
    },

    async executeAction() {
      if (!this.reviewToAction) return;
      
      const success = await this.rejectReviewAPI(this.reviewToAction.review_id);
      
      if (success) {
        this.reviewToAction = null;
      }
    },

    // UI Methods
    viewReviewDetails(review) {
      this.selectedReview = review;
      this.showDetailsModal = true;
    },

    closeDetailsModal() {
      this.showDetailsModal = false;
      this.selectedReview = null;
    },

    closeConfirmModal() {
      this.showConfirmModal = false;
      this.reviewToAction = null;
    },

    getRowClass(review) {
      if (review.status === 'pending') {
        return 'row-pending';
      } else if (review.status === 'rejected') {
        return 'row-rejected';
      }
      return '';
    },

    formatStatus(status) {
      const statusMap = {
        'pending': 'Pending',
        'approved': 'Approved',
        'rejected': 'Rejected'
      };
      return statusMap[status] || status;
    },

    getStatusIcon(status) {
      const icons = {
        'approved': 'fa-solid fa-check-circle',
        'pending': 'fa-solid fa-clock',
        'rejected': 'fa-solid fa-ban'
      };
      return icons[status] || 'fa-solid fa-clock';
    },

    getRatingColor(rating) {
      const colors = {
        1: '#ff6b6b',
        2: '#ffa726',
        3: '#ffc107',
        4: '#4ecdc4',
        5: '#54d62c'
      };
      return colors[rating] || '#ffc107';
    },

    getRatingLabel(rating) {
      const labels = {
        1: 'Poor',
        2: 'Fair',
        3: 'Good',
        4: 'Very Good',
        5: 'Excellent'
      };
      return labels[rating] || 'Good';
    },

    // Pagination Methods
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    goToPage(page) {
      this.currentPage = page;
    },

    resetPagination() {
      this.currentPage = 1;
    },

    // Helper Methods
    getInitials(name) {
      if (!name) return 'U';
      return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    },

    formatDate(dateString) {
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        });
      } catch (error) {
        return 'Unknown date';
      }
    },

    formatTime(dateString) {
      try {
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit' 
        });
      } catch (error) {
        return 'Unknown time';
      }
    },

    truncateText(text, maxLength) {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    },

  }
};
</script>
<style scoped>
.access-denied-card {
  max-width: 420px;
  margin: 60px auto;
  padding: 32px;
  background: var(--background-color2);
  border-radius: 12px;
  border: 1px solid var(--border-color2);
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
.access-denied-icon {
  font-size: 48px;
  color: #f59e0b;
  margin-bottom: 16px;
}
.access-denied-card h3 {
  font-size: 1.25rem;
  color: var(--text-color1);
  margin: 0 0 12px 0;
}
.access-denied-card p {
  font-size: 0.9375rem;
  color: var(--text-color2);
  margin: 0 0 8px 0;
  line-height: 1.5;
}
.access-denied-hint {
  font-size: 0.8125rem;
  opacity: 0.85;
}

.admin-reviews-container {
  min-height: 100vh;
  background-color: var(--background-color1);
  color: var(--text-color1);
}

.admin-content {
  padding: 20px;
  max-width: 1600px;
  margin: 0 auto;
}

/* HEADER */
.admin-header {
  margin-bottom: 30px;
}

.admin-header h1 {
  color: var(--text-color1);
  font-size: 28px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.admin-header h1 i {
  color: var(--primary-color);
}

.subtitle {
  color: var(--text-color2);
  font-size: 16px;
}

/* STATS CARDS */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  background: var(--background-color2);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 20px;
  box-shadow: 0 4px 12px var(--box-shadow);
  border: 1px solid var(--background-color3);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 16px var(--box-shadow);
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.stat-icon.total {
  background: rgba(84, 214, 44, 0.1);
  color: #54d62c;
}

.stat-icon.pending {
  background: rgba(255, 193, 7, 0.1);
  color: #ffc107;
}

.stat-icon.approved {
  background: rgba(40, 167, 69, 0.1);
  color: #28a745;
}

.stat-icon.rejected {
  background: rgba(220, 53, 69, 0.1);
  color: #dc3545;
}

.stat-content h3 {
  font-size: 32px;
  color: var(--text-color1);
  margin-bottom: 4px;
}

.stat-content p {
  color: var(--text-color2);
  font-size: 14px;
}

/* CONTROLS SECTION */
.controls-section {
  background: var(--background-color2);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 25px;
  box-shadow: 0 2px 8px var(--box-shadow);
  border: 1px solid var(--background-color3);
}

.filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 20px;
  align-items: center;
  justify-content: space-between;
}

.search-box {
  flex: 1;
  min-width: 300px;
  position: relative;
}

.search-box i {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-color2);
}

.search-box input {
  width: 100%;
  padding: 12px 15px 12px 45px;
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  border-radius: 8px;
  color: var(--text-color1);
  font-size: 14px;
  transition: border-color 0.3s ease;
}

.search-box input:focus {
  outline: none;
  border-color: var(--border-color-hover);
}

.status-badge.status-pending {
  background: rgba(255, 193, 7, 0.1);
  color: #ffc107;
  border: 1px solid rgba(255, 193, 7, 0.3);
}

.status-badge.status-approved {
  background: rgba(40, 167, 69, 0.1);
  color: #28a745;
  border: 1px solid rgba(40, 167, 69, 0.3);
}

.status-badge.status-rejected {
  background: rgba(220, 53, 69, 0.1);
  color: #dc3545;
  border: 1px solid rgba(220, 53, 69, 0.3);
}

/* Row styling */
.row-pending {
  background: rgba(255, 193, 7, 0.05) !important;
}

.row-rejected {
  background: rgba(220, 53, 69, 0.05) !important;
}

.row-pending:hover {
  background: rgba(255, 193, 7, 0.1) !important;
}

.row-rejected:hover {
  background: rgba(220, 53, 69, 0.1) !important;
}

.filter-group {
  display: flex;
  gap: 12px;
  align-items: center;
}

.filter-select {
  padding: 12px 35px 12px 15px;
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  border-radius: 8px;
  color: var(--text-color1);
  font-size: 14px;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23888' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14L2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
}

.filter-select:focus {
  outline: none;
  border-color: var(--border-color-hover);
}

.btn-refresh {
  padding: 12px 20px;
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  border-radius: 8px;
  color: var(--text-color1);
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.btn-refresh:hover {
  background: var(--background-color3);
  border-color: var(--border-color-hover);
}

.btn-refresh:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.date-filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  align-items: center;
  padding-top: 15px;
  border-top: 1px solid var(--background-color3);
}

.date-input-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.date-input-group label {
  color: var(--text-color2);
  font-size: 14px;
  white-space: nowrap;
}

.date-input-group input {
  padding: 10px;
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  border-radius: 6px;
  color: var(--text-color1);
  font-size: 14px;
}

.date-input-group input:focus {
  outline: none;
  border-color: var(--border-color-hover);
}

.btn-clear-filters {
  padding: 10px 20px;
  background: transparent;
  border: 1px solid var(--background-color3);
  border-radius: 6px;
  color: var(--text-color2);
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  margin-left: auto;
}

.btn-clear-filters:hover {
  border-color: var(--border-color-hover);
  color: var(--text-color1);
}

/* REVIEWS TABLE */
.reviews-table-container {
  background: var(--background-color2);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px var(--box-shadow);
  border: 1px solid var(--background-color3);
}

.loading-table,
.no-reviews {
  padding: 60px 20px;
  text-align: center;
  color: var(--text-color2);
}

.loading-table i {
  font-size: 32px;
  margin-bottom: 15px;
  color: var(--primary-color);
}

.no-reviews i {
  font-size: 48px;
  margin-bottom: 15px;
  color: var(--text-color2);
}

.no-reviews h3 {
  color: var(--text-color1);
  margin-bottom: 8px;
}

.table-responsive {
  overflow-x: auto;
}

.reviews-table {
  width: 100%;
  border-collapse: collapse;
}

.reviews-table th {
  background: var(--background-color1);
  color: var(--text-color2);
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 16px 12px;
  text-align: left;
  border-bottom: 1px solid var(--background-color3);
  white-space: nowrap;
}

.reviews-table td {
  padding: 16px 12px;
  border-bottom: 1px solid var(--background-color3);
  vertical-align: middle;
}

.reviews-table tbody tr {
  transition: background-color 0.2s ease;
}

.reviews-table tbody tr:hover {
  background: var(--background-color3);
}

.reviews-table tbody tr.row-pending {
  background: rgba(255, 193, 7, 0.05);
}

.reviews-table tbody tr.row-pending:hover {
  background: rgba(255, 193, 7, 0.1);
}

/* Table Cell Styles */
.review-id {
  font-family: monospace;
  color: var(--text-color2);
  font-weight: 500;
}

.seller-info,
.reviewer-info {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 180px;
}

.seller-avatar,
.reviewer-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--primary-color);
  color: var(--text-color3);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
}

.seller-details,
.reviewer-details {
  display: flex;
  flex-direction: column;
}

.seller-details strong,
.reviewer-details strong {
  color: var(--text-color1);
  font-size: 14px;
}

.seller-details small,
.reviewer-details small {
  color: var(--text-color2);
  font-size: 12px;
}

.rating-cell {
  min-width: 150px;
}

.star-display {
  display: flex;
  gap: 2px;
  margin-bottom: 4px;
}

.star-display i {
  font-size: 14px;
}

.rating-text {
  color: var(--text-color2);
  font-size: 13px;
}

.review-content-cell {
  min-width: 250px;
  max-width: 300px;
}

.review-content strong {
  color: var(--text-color1);
  font-size: 14px;
  display: block;
  margin-bottom: 4px;
}

.review-comment {
  color: var(--text-color2);
  font-size: 13px;
  line-height: 1.4;
  margin-bottom: 6px;
}

.verified-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(84, 214, 44, 0.1);
  color: #54d62c;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
}

.verified-badge i {
  font-size: 10px;
}

.status-cell {
  min-width: 120px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.status-pending {
  background: rgba(255, 193, 7, 0.1);
  color: #ffc107;
  border: 1px solid rgba(255, 193, 7, 0.3);
}

.status-approved {
  background: rgba(40, 167, 69, 0.1);
  color: #28a745;
  border: 1px solid rgba(40, 167, 69, 0.3);
}

.date-cell {
  min-width: 120px;
  white-space: nowrap;
  color: var(--text-color2);
  font-size: 13px;
}

.date-cell small {
  color: var(--text-color2);
  opacity: 0.7;
}

.actions-cell {
  min-width: 150px;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.btn-action {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--background-color3);
  background: var(--background-color1);
  color: var(--text-color2);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
}

.btn-action:hover:not(:disabled) {
  transform: translateY(-1px);
}

.btn-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-view:hover:not(:disabled) {
  background: rgba(33, 150, 243, 0.1);
  color: #2196f3;
  border-color: #2196f3;
}

.btn-approve:hover:not(:disabled) {
  background: rgba(40, 167, 69, 0.1);
  color: #28a745;
  border-color: #28a745;
}

.btn-reject:hover:not(:disabled) {
  background: rgba(220, 53, 69, 0.1);
  color: #dc3545;
  border-color: #dc3545;
}

.btn-delete:hover:not(:disabled) {
  background: rgba(220, 53, 69, 0.1);
  color: #dc3545;
  border-color: #dc3545;
}

/* PAGINATION */
.pagination-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  background: var(--background-color2);
  border-top: 1px solid var(--background-color3);
  gap: 15px;
}

.pagination-info {
  color: var(--text-color2);
  font-size: 14px;
}

.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-btn {
  padding: 8px 16px;
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  border-radius: 6px;
  color: var(--text-color1);
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  background: var(--background-color3);
  border-color: var(--border-color-hover);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: 4px;
}

.page-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  border-radius: 6px;
  color: var(--text-color1);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.page-btn:hover {
  background: var(--background-color3);
}

.page-btn.active {
  background: var(--primary-color);
  color: var(--text-color3);
  border-color: var(--primary-color);
}

.page-size-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-size-selector label {
  color: var(--text-color2);
  font-size: 14px;
}

.page-size-select {
  padding: 8px 30px 8px 12px;
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  border-radius: 6px;
  color: var(--text-color1);
  font-size: 14px;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23888' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14L2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;
  background-size: 12px;
}

/* MODALS */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-content {
  background: var(--background-color2);
  border-radius: 16px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease;
  border: 1px solid var(--background-color3);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  padding: 24px;
  border-bottom: 1px solid var(--background-color3);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-header h3 {
  color: var(--text-color1);
  font-size: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.modal-header h3 i {
  color: var(--primary-color);
}

.modal-close {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  color: var(--text-color2);
  cursor: pointer;
  transition: all 0.2s ease;
}

.modal-close:hover {
  background: var(--background-color3);
  border-color: var(--border-color-hover);
  color: var(--text-color1);
}

.modal-body {
  padding: 24px;
}

/* REVIEW DETAILS MODAL */
.review-details {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.details-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--background-color3);
}

.review-id-badge {
  background: var(--background-color1);
  color: var(--text-color2);
  padding: 6px 12px;
  border-radius: 6px;
  font-family: monospace;
  font-size: 14px;
  border: 1px solid var(--background-color3);
}

.review-status {
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
}

.review-status.status-approved {
  background: rgba(40, 167, 69, 0.1);
  color: #28a745;
  border: 1px solid rgba(40, 167, 69, 0.3);
}

.review-status.status-pending {
  background: rgba(255, 193, 7, 0.1);
  color: #ffc107;
  border: 1px solid rgba(255, 193, 7, 0.3);
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

.info-card {
  background: var(--background-color1);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid var(--background-color3);
}

.info-card h4 {
  color: var(--text-color1);
  font-size: 16px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-card h4 i {
  color: var(--primary-color);
}

.info-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.avatar-large {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: var(--primary-color);
  color: var(--text-color3);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 18px;
  flex-shrink: 0;
}

.info-content h5 {
  color: var(--text-color1);
  font-size: 18px;
  margin-bottom: 4px;
}

.info-content p {
  color: var(--text-color2);
  font-size: 14px;
  margin-bottom: 8px;
}

.verified-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(84, 214, 44, 0.1);
  color: #54d62c;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.rating-section h4,
.review-content-section h4,
.admin-actions-section h4 {
  color: var(--text-color1);
  font-size: 18px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.rating-section h4 i,
.review-content-section h4 i,
.admin-actions-section h4 i {
  color: var(--primary-color);
}

.rating-display-large {
  display: flex;
  align-items: center;
  gap: 20px;
}

.stars-large {
  display: flex;
  gap: 4px;
}

.stars-large i {
  font-size: 28px;
}

.rating-text-large {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.rating-number {
  font-size: 32px;
  font-weight: 700;
  color: var(--text-color1);
}

.rating-out-of {
  font-size: 20px;
  color: var(--text-color2);
}

.rating-label {
  font-size: 16px;
  color: var(--text-color2);
  margin-left: 8px;
}

.review-content-card {
  background: var(--background-color1);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid var(--background-color3);
}

.review-content-card h5 {
  color: var(--text-color1);
  font-size: 18px;
  margin-bottom: 12px;
}

.full-review {
  color: var(--text-color2);
  line-height: 1.6;
  margin-bottom: 16px;
  white-space: pre-wrap;
}

.review-meta {
  display: flex;
  gap: 20px;
  color: var(--text-color2);
  font-size: 14px;
}

.review-meta span {
  display: flex;
  align-items: center;
  gap: 6px;
}

.review-meta i {
  color: var(--primary-color);
}

.admin-actions-section {
  padding-top: 20px;
  border-top: 1px solid var(--background-color3);
}

.action-buttons-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.btn-modal {
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  border: 1px solid transparent;
}

.btn-modal:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-modal-approve {
  background: rgba(40, 167, 69, 0.1);
  color: #28a745;
  border-color: rgba(40, 167, 69, 0.3);
}

.btn-modal-approve:hover:not(:disabled) {
  background: rgba(40, 167, 69, 0.2);
  transform: translateY(-1px);
}

.btn-modal-reject {
  background: rgba(220, 53, 69, 0.1);
  color: #dc3545;
  border-color: rgba(220, 53, 69, 0.3);
}

.btn-modal-reject:hover:not(:disabled) {
  background: rgba(220, 53, 69, 0.2);
  transform: translateY(-1px);
}

.btn-modal-delete {
  background: rgba(220, 53, 69, 0.1);
  color: #dc3545;
  border-color: rgba(220, 53, 69, 0.3);
}

.btn-modal-delete:hover:not(:disabled) {
  background: rgba(220, 53, 69, 0.2);
  transform: translateY(-1px);
}

/* CONFIRMATION MODAL */
.confirm-modal {
  background: var(--background-color2);
  border-radius: 16px;
  width: 100%;
  max-width: 500px;
  overflow: hidden;
  border: 1px solid var(--background-color3);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;
}

.confirm-header {
  padding: 30px 30px 20px;
  text-align: center;
  border-bottom: 1px solid var(--background-color3);
}

.confirm-header i {
  font-size: 48px;
  margin-bottom: 16px;
}

.confirm-header h3 {
  color: var(--text-color1);
  font-size: 22px;
  margin: 0;
}

.confirm-body {
  padding: 30px;
  text-align: center;
}

.confirm-body p {
  color: var(--text-color2);
  line-height: 1.6;
  margin-bottom: 20px;
}

.review-preview {
  background: var(--background-color1);
  border-radius: 8px;
  padding: 16px;
  text-align: left;
  margin-top: 20px;
  border: 1px solid var(--background-color3);
}

.review-preview p {
  margin-bottom: 8px;
  color: var(--text-color2);
}

.review-preview strong {
  color: var(--text-color1);
}

.confirm-footer {
  padding: 20px 30px;
  border-top: 1px solid var(--background-color3);
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn-cancel {
  padding: 10px 24px;
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  border-radius: 8px;
  color: var(--text-color2);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-cancel:hover:not(:disabled) {
  background: var(--background-color3);
  border-color: var(--border-color-hover);
  color: var(--text-color1);
}

.btn-cancel:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-confirm {
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-confirm:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-confirm-approve {
  background: #28a745;
  color: white;
}

.btn-confirm-approve:hover:not(:disabled) {
  background: #218838;
  transform: translateY(-1px);
}

.btn-confirm-reject {
  background: #dc3545;
  color: white;
}

.btn-confirm-reject:hover:not(:disabled) {
  background: #c82333;
  transform: translateY(-1px);
}

.btn-confirm-delete {
  background: #dc3545;
  color: white;
}

.btn-confirm-delete:hover:not(:disabled) {
  background: #c82333;
  transform: translateY(-1px);
}

/* TOAST NOTIFICATION */
.toast-notification {
  position: fixed;
  bottom: 30px;
  right: 30px;
  background: var(--background-color2);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  max-width: 400px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
  border: 1px solid var(--background-color3);
  z-index: 1001;
  animation: slideInRight 0.3s ease;
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.toast-notification i {
  font-size: 24px;
  flex-shrink: 0;
}

.toast-notification.success i {
  color: #28a745;
}

.toast-notification.error i {
  color: #dc3545;
}

.toast-notification.warning i {
  color: #ffc107;
}

.toast-notification.info i {
  color: #17a2b8;
}

.toast-content {
  flex: 1;
}

.toast-title {
  color: var(--text-color1);
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 4px;
}

.toast-message {
  color: var(--text-color2);
  font-size: 14px;
  line-height: 1.4;
}

.toast-close {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid var(--background-color3);
  color: var(--text-color2);
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.toast-close:hover {
  background: var(--background-color3);
  border-color: var(--border-color-hover);
  color: var(--text-color1);
}

/* RESPONSIVE DESIGN */
@media (max-width: 1200px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .admin-content {
    padding: 15px;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .filters-row {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-box {
    min-width: 100%;
  }
  
  .filter-group {
    width: 100%;
    justify-content: stretch;
  }
  
  .filter-select {
    flex: 1;
  }
  
  .date-filter-row {
    flex-direction: column;
    align-items: stretch;
  }
  
  .date-input-group {
    flex-direction: column;
    align-items: stretch;
  }
  
  .btn-clear-filters {
    margin-left: 0;
  }
  
  .pagination-controls {
    flex-direction: column;
    align-items: stretch;
  }
  
  .pagination-buttons {
    justify-content: center;
  }
  
  .page-size-selector {
    justify-content: center;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
  
  .action-buttons-row {
    flex-direction: column;
  }
  
  .btn-modal {
    width: 100%;
    justify-content: center;
  }
  
  .confirm-footer {
    flex-direction: column;
  }
  
  .btn-cancel,
  .btn-confirm {
    width: 100%;
    justify-content: center;
  }
  
  .toast-notification {
    left: 15px;
    right: 15px;
    max-width: none;
    bottom: 15px;
  }
}

@media (max-width: 480px) {
  .admin-header h1 {
    font-size: 24px;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .reviews-table {
    font-size: 13px;
  }
  
  .seller-info,
  .reviewer-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .action-buttons {
    flex-wrap: wrap;
  }
  
  .modal-content,
  .confirm-modal {
    margin: 10px;
    max-height: 85vh;
  }
  
  .modal-header,
  .modal-body,
  .confirm-header,
  .confirm-body,
  .confirm-footer {
    padding: 15px;
  }
}
</style>